/* basyd – product facts drawers v4 */
(() => {
  'use strict';

  class BasydFlavorFacts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.basydFactsReady === 'true' || this.dataset.basydFactsReady === 'pending') return;
      this.dataset.basydFactsReady = 'pending';

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.initialize();
      });
    }

    initialize() {
      this.variantMap = this.readVariantMap();
      this.currentVariantId = Number(this.dataset.initialVariantId || 0);
      this.productFormId = this.dataset.productFormId || '';
      this.mixIntro = this.dataset.mixIntro || 'Wähle eine Geschmacksrichtung, um die passenden Angaben zu sehen.';
      this.drawers = {
        ingredients: document.getElementById(this.dataset.ingredientsDrawerId || ''),
        nutrition: document.getElementById(this.dataset.nutritionDrawerId || ''),
        amino: document.getElementById(this.dataset.aminoDrawerId || '')
      };

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);

      document.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('product:variant-change', this.onVariantChange);
      document.addEventListener('change', this.onDocumentChange);
      this.addEventListener('click', this.onTriggerClick, true);

      Object.values(this.drawers).filter(Boolean).forEach((drawer) => {
        drawer.addEventListener('click', this.onDrawerClick);
      });

      this.dataset.basydFactsReady = 'true';
      this.syncVariantFromForm();
      this.renderAll();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('product:variant-change', this.onVariantChange);
      document.removeEventListener('change', this.onDocumentChange);
      this.removeEventListener('click', this.onTriggerClick, true);

      if (this.drawers) {
        Object.values(this.drawers).filter(Boolean).forEach((drawer) => {
          drawer.removeEventListener('click', this.onDrawerClick);
        });
      }
    }

    readVariantMap() {
      const script = this.querySelector('[data-basyd-facts-variant-map]');
      if (!script) return new Map();

      try {
        const rows = JSON.parse(script.textContent || '[]');
        return new Map(rows.map((row) => [Number(row.id), row]));
      } catch (error) {
        console.error('basyd Flavor Facts: Variantenzuordnung konnte nicht gelesen werden.', error);
        return new Map();
      }
    }

    getProductForm() {
      if (!this.productFormId) return null;
      return document.getElementById(this.productFormId) || document.forms[this.productFormId] || null;
    }

    syncVariantFromForm() {
      const form = this.getProductForm();
      const input = form?.querySelector('input[name="id"]');
      const id = Number(input?.value || 0);

      if (id) this.currentVariantId = id;
      return id;
    }

    getTemplates(mode) {
      return Array.from(this.querySelectorAll(`template[data-basyd-facts-template="${mode}"]`));
    }

    findTemplate(mode, handle) {
      if (!handle) return null;
      return this.getTemplates(mode).find((template) => template.dataset.flavorHandle === handle) || null;
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' und ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    getSelectedFlavorName() {
      const section = this.closest('.shopify-section--main-product') || document.querySelector('.shopify-section--main-product');
      if (!section) return '';

      const fieldset = section.querySelector('.variant-picker__option[data-basyd-option-role="flavor"]');
      if (!fieldset) return '';

      const input = fieldset.querySelector('input[type="radio"]:checked');
      if (input) {
        const label = input.labels?.[0] || null;
        const srOnly = label?.querySelector('.sr-only');
        const value = (srOnly?.textContent || label?.textContent || input.value || '').trim();
        if (value) return value;
      }

      return (fieldset.dataset.basydSelectedValue || '').trim();
    }

    findTemplateByFlavorName(mode, flavorName) {
      const needle = this.normalize(flavorName);
      if (!needle) return null;

      const templates = this.getTemplates(mode);
      const exact = templates.find((template) => this.normalize(template.dataset.flavorName) === needle);
      if (exact) return exact;

      return templates.find((template) => {
        const candidate = this.normalize(template.dataset.flavorName);
        if (!candidate || candidate.length < 4 || needle.length < 4) return false;
        return candidate.includes(needle) || needle.includes(candidate);
      }) || null;
    }

    resolveTemplate(mode) {
      /*
        Primary source: exact variant → metafield → metaobject mapping.
        Cross-check: the flavor currently visible/selected in the PDP. This is
        intentionally a safety net for Stretch's asynchronous partial rerendering.
      */
      const variant = this.variantMap.get(Number(this.currentVariantId));
      const mappedTemplate = this.findTemplate(mode, variant?.factsHandle);
      const visibleFlavor = this.getSelectedFlavorName();
      const visibleTemplate = this.findTemplateByFlavorName(mode, visibleFlavor);

      if (visibleTemplate) return visibleTemplate;
      return mappedTemplate;
    }

    onVariantChange(event) {
      const variantId = Number(event?.detail?.variant?.id || 0);
      if (variantId) this.currentVariantId = variantId;
      else this.syncVariantFromForm();
      this.renderAll();
    }

    onDocumentChange(event) {
      const form = this.getProductForm();
      const target = event.target;
      if (!target) return;

      /* Stretch changes the hidden variant ID and dispatches a bubbling change event. */
      if (form && target.name === 'id' && form.contains(target)) {
        const id = Number(target.value || 0);
        if (id) this.currentVariantId = id;
        this.renderAll();
        return;
      }

      /* Immediate visual sync when the customer clicks a flavor swatch. */
      if (target.matches?.('.variant-picker__option[data-basyd-option-role="flavor"] input[type="radio"]')) {
        this.renderAll();
        [0, 80, 250].forEach((delay) => {
          window.setTimeout(() => {
            if (!this.isConnected) return;
            this.syncVariantFromForm();
            this.renderAll();
          }, delay);
        });
      }
    }

    onTriggerClick(event) {
      if (!event.target.closest('[data-basyd-facts-trigger]')) return;
      this.syncVariantFromForm();
      this.renderAll();
    }

    renderAll() {
      this.renderMode('ingredients');
      this.renderMode('nutrition');
    }

    renderMode(mode) {
      const drawer = this.drawers?.[mode];
      if (!drawer) return;

      const slot = drawer.querySelector(`[data-basyd-facts-slot="${mode}"]`);
      if (!slot) return;

      /* Never leave data from the previously selected flavor in place. */
      slot.replaceChildren();

      const template = this.resolveTemplate(mode);
      if (template) {
        slot.appendChild(template.content.cloneNode(true));
        return;
      }

      const templates = this.getTemplates(mode);
      if (templates.length) {
        slot.appendChild(this.buildFlavorSelector(mode, templates));
      } else {
        const empty = document.createElement('p');
        empty.className = 'basyd-facts-empty';
        empty.textContent = 'Für diese Variante sind aktuell keine Produktinformationen hinterlegt.';
        slot.appendChild(empty);
      }
    }

    buildFlavorSelector(mode, templates) {
      const wrapper = document.createElement('div');
      wrapper.className = 'basyd-facts-mix';
      wrapper.dataset.basydFactsMix = mode;

      const intro = document.createElement('p');
      intro.className = 'basyd-facts-mix__intro';
      intro.textContent = this.mixIntro;
      wrapper.appendChild(intro);

      const tabs = document.createElement('div');
      tabs.className = 'basyd-facts-mix__tabs';
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', 'Geschmacksrichtung auswählen');

      const panel = document.createElement('div');
      panel.className = 'basyd-facts-mix__panel';
      panel.dataset.basydFactsMixPanel = '';

      templates.forEach((template, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'basyd-facts-mix__tab';
        button.dataset.basydFactsMixTab = template.dataset.flavorHandle || '';
        button.dataset.basydFactsMode = mode;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.style.setProperty('--tab-gradient-start', template.dataset.gradientStart || '#111111');
        button.style.setProperty('--tab-gradient-end', template.dataset.gradientEnd || '#111111');

        const label = document.createElement('span');
        label.textContent = template.dataset.flavorName || 'Sorte';
        button.appendChild(label);
        tabs.appendChild(button);
      });

      wrapper.appendChild(tabs);
      wrapper.appendChild(panel);

      const firstTemplate = templates[0];
      if (firstTemplate) panel.appendChild(firstTemplate.content.cloneNode(true));

      return wrapper;
    }

    onDrawerClick(event) {
      const tab = event.target.closest('[data-basyd-facts-mix-tab]');
      if (!tab) return;

      const mode = tab.dataset.basydFactsMode;
      const handle = tab.dataset.basydFactsMixTab;
      const drawer = tab.closest('[data-basyd-facts-drawer]');
      if (!drawer || !mode || !handle) return;

      const panel = drawer.querySelector('[data-basyd-facts-mix-panel]');
      const template = this.findTemplate(mode, handle);
      if (!panel || !template) return;

      drawer.querySelectorAll('[data-basyd-facts-mix-tab]').forEach((candidate) => {
        candidate.setAttribute('aria-selected', candidate === tab ? 'true' : 'false');
      });

      panel.replaceChildren(template.content.cloneNode(true));
    }
  }

  if (!customElements.get('basyd-flavor-facts')) {
    customElements.define('basyd-flavor-facts', BasydFlavorFacts);
  }
})();
